/**
 * Generic per-field validator driven by FormFieldConfig.
 *
 * Returns an error string or null. Never trusts a backend-supplied pattern
 * for `phone` or `email` — those always use hardened, frontend-owned regexes
 * (defense in depth against a misconfigured admin authoring a too-permissive
 * pattern).
 */

const patternCache = new Map();
const compile = (src) => {
  if (!src) return null;
  if (patternCache.has(src)) return patternCache.get(src);
  try {
    const re = new RegExp(src);
    patternCache.set(src, re);
    return re;
  } catch {
    patternCache.set(src, null);
    return null;
  }
};

const HARDENED = {
  // 10-digit Indian mobile — matches the pre-migration validator.
  phone: /^[6-9]\d{9}$/,
  // 6-14 digits. Must match the input's maxLength, or a value could pass
  // validation that the field will not let anyone type.
  phoneIntl: /^\d{6,14}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // phoneRule "ae_mobile": UAE mobile, national format 5XXXXXXXX or
  // 05XXXXXXXX (max 10 digits). The server normalises both to 9715XXXXXXXX.
  aeMobile: /^0?5\d{8}$/,
};

// emailRule "business": personal/free mailbox providers. Matched on the
// domain's provider label so every TLD variant (yahoo.co.in, hotmail.co.uk,
// outlook.ae, …) is covered by one entry.
const FREE_MAIL_PROVIDERS = new Set([
  "gmail", "googlemail", "yahoo", "ymail", "rocketmail", "hotmail",
  "outlook", "live", "msn", "icloud", "me", "mac", "aol", "proton",
  "protonmail", "pm", "gmx", "mail", "yandex", "zoho", "zohomail",
  "rediffmail", "inbox", "tutanota", "tuta", "mailinator", "fastmail",
]);
const isFreeMailDomain = (email) => {
  const domain = String(email).split("@").pop().toLowerCase();
  return FREE_MAIL_PROVIDERS.has(domain.split(".")[0]);
};


/**
 * @param field {FormFieldConfig}
 * @param rawValue {string}
 * @returns {string | null} error message or null if valid
 */
export function validateField(field, rawValue) {
  const value = typeof rawValue === "string" ? rawValue.trim() : "";

  if (field.required && !value) {
    return `${field.label} is required`;
  }
  if (!value) return null; // optional + empty = OK

  const v = field.validation || {};

  if (v.minLength != null && value.length < v.minLength) {
    return `${field.label} must be at least ${v.minLength} characters`;
  }
  if (v.maxLength != null && value.length > v.maxLength) {
    return `${field.label} must be at most ${v.maxLength} characters`;
  }

  // Hardened regexes take precedence for sensitive types.
  if (field.type === "phone") {
    // Page-specific national rule takes precedence over the loose
    // international check (frontend-owned, never a backend regex).
    if (field.phoneRule === "ae_mobile") {
      if (!HARDENED.aeMobile.test(value)) {
        return "Enter a valid UAE mobile number starting with 5 or 05 (max 10 digits)";
      }
      return null;
    }
    // International pages accept any national number; libphonenumber is not
    // on the frontend, so this stays a loose sanity check and the server
    // does the authoritative parse (getPhoneDetails) using the isoCode.
    if (field.international) {
      if (!HARDENED.phoneIntl.test(value)) {
        return "Enter a valid mobile number";
      }
      return null;
    }
    if (!HARDENED.phone.test(value)) {
      return "Enter a valid 10-digit Indian mobile number";
    }
    return null;
  }
  if (field.type === "email") {
    if (!HARDENED.email.test(value)) {
      return "Enter a valid email address";
    }
    if (field.emailRule === "business" && isFreeMailDomain(value)) {
      return "Please use your company email (e.g. name@yourcompany.com). Gmail, Yahoo and other personal emails are not accepted.";
    }
    return null;
  }

  if (v.pattern) {
    const re = compile(v.pattern);
    if (re && !re.test(value)) {
      return v.patternMessage || `${field.label} is invalid`;
    }
  }

  return null;
}

/**
 * Validate every field in the config against the current form values.
 * @returns an object { [fieldKey]: errorMessage } of only the failing fields.
 */
export function validateAll(fields, values) {
  const errors = {};
  for (const field of fields) {
    const err = validateField(field, values[field.key]);
    if (err) errors[field.key] = err;
  }
  return errors;
}
